import { useRef, useState } from "react";
import { UploadSimple, CheckCircle, X, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * DocumentUploader
 * ----------------
 * Mobile-first single-file uploader that converts the picked file to a
 * base64 data URL (<= 1.5 MB after compression) and emits it via onChange.
 *
 * Props
 *  - label:    Human label shown above the dropzone
 *  - required: whether to show the red asterisk
 *  - value:    current data URL (string) or empty
 *  - onChange: (dataUrl) => void
 *  - tid:      data-testid prefix
 */
export default function DocumentUploader({ label, required, value, onChange, tid }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$|^application\/pdf$/i.test(file.type)) {
      toast.error("Please upload an image (PNG / JPG / WebP) or PDF");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large — keep it under 5 MB");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await fileToCompressedDataUrl(file);
      onChange(dataUrl);
      toast.success(`${label} uploaded`);
    } catch (e) {
      toast.error("Failed to read file");
    } finally {
      setBusy(false);
    }
  };

  const clear = () => {
    onChange("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const hasFile = !!(value && value.length > 30);
  const isPdf = hasFile && value.startsWith("data:application/pdf");

  return (
    <div className="majhe-doc-uploader">
      <div className="flex items-center justify-between mb-1.5 px-1">
        <label className="text-[12px] font-bold text-[#18142A]" htmlFor={`${tid}-input`}>
          {label} {required && <span className="text-[#DC2626]">*</span>}
        </label>
        {hasFile && (
          <button type="button" onClick={clear} data-testid={`${tid}-clear`}
            aria-label={`Remove uploaded ${label}`}
            className="text-[11px] font-bold text-[#7B7390] flex items-center gap-1">
            <X size={12} weight="bold"/> Remove
          </button>
        )}
      </div>

      <input ref={fileRef} type="file" id={`${tid}-input`} data-testid={`${tid}-input`}
        accept="image/png,image/jpeg,image/webp,application/pdf"
        capture="environment"
        className="hidden" aria-label={`Upload ${label}`}
        onChange={(e) => pick(e.target.files?.[0])}/>

      {!hasFile && (
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
          data-testid={`${tid}-btn`}
          aria-label={`Tap to upload ${label}`}
          className="w-full rounded-xl border-2 border-dashed border-[#C9B5FF] bg-[#F5F2FF]
            py-4 px-3 flex items-center gap-3 text-left transition-colors hover:bg-[#EDE5FF]">
          <div className="w-10 h-10 rounded-lg bg-white text-[#5B2EE5] flex items-center justify-center shrink-0">
            <UploadSimple size={20} weight="bold"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold text-[#4920C2]">
              {busy ? "Uploading…" : `Tap to upload ${label.toLowerCase()}`}
            </div>
            <div className="text-[10.5px] text-[#7B7390] mt-0.5">
              Camera or gallery · JPG / PNG / PDF up to 5 MB
            </div>
          </div>
        </button>
      )}

      {hasFile && (
        <div className="w-full rounded-xl border border-[#5B2EE5] bg-white p-3 flex items-center gap-3"
          data-testid={`${tid}-preview`}>
          {isPdf ? (
            <div className="w-12 h-12 rounded-lg bg-[#EDE5FF] text-[#5B2EE5] flex items-center justify-center shrink-0">
              <FileText size={22} weight="bold"/>
            </div>
          ) : (
            <img src={value} alt={`${label} preview`}
              className="w-12 h-12 object-cover rounded-lg border border-[#EDE5FF] shrink-0"/>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold text-[#2D1170] flex items-center gap-1">
              <CheckCircle size={14} weight="fill" className="text-[#5B2EE5]"/>
              {label} ready
            </div>
            <div className="text-[10.5px] text-[#7B7390] mt-0.5">
              {isPdf ? "PDF document" : "Image"} · will be sent for verification
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Compress image into a JPEG data URL <= ~1.5 MB. PDFs pass through unchanged. */
async function fileToCompressedDataUrl(file) {
  if (file.type === "application/pdf") {
    return await readAsDataUrl(file);
  }
  // Image — downscale via canvas
  const dataUrl = await readAsDataUrl(file);
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const maxDim = 1400;
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
}
